import { CommonModule } from '@angular/common';
import { Component, computed, input, output } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NavigationConfig, NavigationEvent } from './interfaces/navigation.interfaces';

/**
 * Generic navigation link component with ship icon support
 * Core component that provides consistent navigation patterns across the application
 */
@Component({
  selector: 'app-navigation-link',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatTooltipModule],
  templateUrl: './navigation-link.component.html',
  styleUrls: ['./navigation-link.component.scss'],
})
export class NavigationLinkComponent<TData = any, TQuery = any> {
  /** Text to display in the navigation link (flat/single-line layout) */
  readonly text = input<string>('');

  /**
   * Code text for stacked layout (bold, first line).
   * When non-empty, activates the stacked CODE (bold) + rocket / NAME (muted) layout
   * that mirrors the canonical CodeNameNavigationCellRendererComponent pattern.
   */
  readonly codeText = input<string>('');

  /**
   * Name text for stacked layout (muted, second line).
   * Rendered below codeText when codeText is non-empty.
   */
  readonly nameText = input<string>('');

  /** True when codeText is provided — activates the stacked two-line layout */
  readonly hasStackedLayout = computed(() => this.codeText().length > 0);

  /**
   * Text rendered in the flat (single-line) layout. Falls back to nameText so a
   * wrapper using displayMode='both' on a record WITHOUT a code still shows the
   * name beside the rocket instead of an empty cell.
   */
  readonly flatText = computed(() => this.text() || this.nameText());

  /** Whether the link is navigable/clickable */
  readonly isNavigable = input<boolean>(false);

  /** Navigation configuration including data, url, and query params */
  readonly navigationConfig = input<NavigationConfig<TData, TQuery>>();

  /** Icon to display (defaults to ship/rocket-launch) */
  readonly icon = input<string>('heroicons_outline:rocket-launch');

  /** Position of icon relative to text */
  readonly iconPosition = input<'left' | 'right'>('right');

  /** Whether to show the icon */
  readonly showIcon = input<boolean>(true);

  /** Whether to show the text */
  readonly showText = input<boolean>(true);

  /** Which parts are clickable */
  readonly interactionMode = input<'both' | 'text-only' | 'icon-only'>('both');

  /** Custom CSS classes */
  readonly customClass = input<string>('');

  /** Tooltip text to display */
  readonly tooltipText = input<string>('');

  /** Event emitted when navigation occurs */
  readonly navigate = output<NavigationEvent<TData, TQuery>>();

  /** Check if text click should be allowed */
  readonly isTextClickable = computed(() =>
    this.isNavigable() && (this.interactionMode() === 'both' || this.interactionMode() === 'text-only')
  );

  /** Check if icon click should be allowed */
  readonly isIconClickable = computed(() =>
    this.isNavigable() && (this.interactionMode() === 'both' || this.interactionMode() === 'icon-only')
  );

  /**
   * Check if the component has something meaningful to render.
   * A bare icon is NOT enough — we must have a stacked code, a navigable
   * target, or actual text. Otherwise we fall back to the "-" placeholder
   * instead of rendering a stray (non-clickable) rocket on empty data.
   */
  readonly hasValidDisplay = computed(() =>
    this.hasStackedLayout() ||
    this.isNavigable() ||
    (this.showText() && !!(this.text() || this.nameText())?.trim())
  );

  /** Get CSS classes for the navigation container */
  readonly containerClasses = computed(() => {
    // items-center keeps the icon and the text aligned on the same vertical
    // axis for single-line cell content (code/name), which is how the link is
    // used across the list grids.
    const baseClasses = 'flex items-center';
    const interactionClasses = this.isNavigable() ? 'cursor-pointer transition-colors' : '';
    const customClasses = this.customClass() || '';

    return [baseClasses, interactionClasses, customClasses]
      .filter((cls) => cls.length > 0)
      .join(' ');
  });

  /** Get CSS classes for the text element */
  readonly textClasses = computed(() => {
    const baseClasses = '';
    const interactionClasses = this.isTextClickable()
      ? 'underline hover:text-blue-600'
      : '';

    return [baseClasses, interactionClasses].filter((cls) => cls.length > 0).join(' ');
  });

  /** Get CSS classes for the icon element (flat layout) */
  readonly iconClasses = computed(() => {
    // icon-size-4 = 1rem gives the icon more visual weight matching the
    // canonical CodeNameNavigationCellRenderer.  mt-px nudges it to align
    // optically with the FIRST text line when text wraps (flat layout).
    const baseClasses = 'icon-size-4 mt-px';
    const positionClasses = this.iconPosition() === 'left' ? 'mr-1' : 'ml-1';
    const interactionClasses = this.isNavigable() ? 'hover:animate-bounce' : '';

    return [baseClasses, positionClasses, interactionClasses]
      .filter((cls) => cls.length > 0)
      .join(' ');
  });

  /** Get CSS classes for the icon inside the stacked code row */
  readonly stackedIconClasses = computed(() => {
    const interactionClasses = this.isNavigable() ? 'hover:animate-bounce hover:text-blue-600 transition-colors' : '';
    return ['icon-size-3 cursor-pointer', interactionClasses]
      .filter((cls) => cls.length > 0)
      .join(' ');
  });

  /** Check if text should have underline */
  readonly shouldUnderlineText = computed(() => this.isTextClickable());

  /** Get appropriate cursor class for text */
  readonly textCursorClass = computed(() => this.isTextClickable() ? 'cursor-pointer' : '');

  /** Get appropriate cursor class for icon */
  readonly iconCursorClass = computed(() => this.isIconClickable() ? 'cursor-pointer' : '');

  /**
   * Handle navigation click.
   * @param forceNewTab When true, overrides the config so the navigation opens
   *        in a new tab (used for Ctrl/Cmd/middle-click), mirroring the native
   *        behavior of a real anchor while keeping the same-tab default.
   */
  handleNavigation(forceNewTab = false): void {
    const config = this.navigationConfig();
    if (!this.isNavigable() || !config) {
      return;
    }

    const effectiveConfig: NavigationConfig<TData, TQuery> = forceNewTab
      ? { ...config, openInNewTab: true }
      : config;

    const navigationEvent: NavigationEvent<TData, TQuery> = {
      config: effectiveConfig,
      timestamp: new Date(),
      source: 'navigation-link',
    };

    this.navigate.emit(navigationEvent);
  }

  /**
   * Whether a click should open the target in a new tab.
   * Ctrl (Windows/Linux) or Cmd (macOS) held down ⇒ new tab.
   */
  private shouldOpenInNewTab(event: MouseEvent): boolean {
    return event.ctrlKey || event.metaKey;
  }

  /**
   * Handle text-only click (when interactionMode is 'text-only' or 'both')
   */
  handleTextClick(event: MouseEvent): void {
    if (this.interactionMode() === 'icon-only') {
      return;
    }
    this.handleNavigation(this.shouldOpenInNewTab(event));
  }

  /**
   * Handle icon-only click (when interactionMode is 'icon-only' or 'both')
   */
  handleIconClick(event: MouseEvent): void {
    if (this.interactionMode() === 'text-only') {
      return;
    }
    this.handleNavigation(this.shouldOpenInNewTab(event));
  }

  /**
   * Handle middle-click (mouse wheel button) on the text ⇒ open in new tab,
   * mirroring native anchor behavior. `auxclick` is the event that fires for
   * non-primary mouse buttons; a regular `click` does not fire for middle-click.
   */
  handleTextAuxClick(event: MouseEvent): void {
    if (event.button !== 1 || this.interactionMode() === 'icon-only') {
      return;
    }
    event.preventDefault();
    this.handleNavigation(true);
  }

  /**
   * Handle middle-click on the icon ⇒ open in new tab.
   */
  handleIconAuxClick(event: MouseEvent): void {
    if (event.button !== 1 || this.interactionMode() === 'text-only') {
      return;
    }
    event.preventDefault();
    this.handleNavigation(true);
  }
}
